/**
 * useLiveTestingCollapsible
 *
 * Live Testing에서 collapsible 타입 질문을 처리하기 위한 훅
 * V1 QsCollapsible/QsCollapsibleGroup/QsCollapsibleGroupItem의 V2 버전
 *
 * 기능:
 * - 접이식 그룹으로 증상 아이템 표시
 * - 그룹 열기/닫기 상태 관리
 * - 아이템 클릭 시 증상 트리의 질문들을 모달에서 표시
 * - 특수 옵션 (NoneAbove, Special) 처리
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { questionTreeControllerFindByTreeKey } from '../../../../api/generated/question-trees/question-trees';
import {
  questionControllerFindByKey,
  questionControllerFindByTreeId,
} from '../../../../api/generated/questions/questions';
import { translationControllerSearch } from '../../../../api/generated/translation/translation';
import type { QuestionResponseDto, TranslationResponseDto } from '../../../../api/generated/types';
import type { LiveTestingAnswerValue } from './types';
import type {
  V2QsCollapsibleGroup,
  V2QsCollapsibleItem,
  V2QsCollapsibleItemType,
} from '../../../../shared/components/question-types/V2QsCollapsible';
import { normalizeTranslatedLabelForSurveyLocale } from '../../../../shared/utils/survey-locale-text';

// "None of the above" 키 상수
const NONE_ABOVE_KEY = 'o-none-above';
// Sexual assault 키 상수
const SEXUAL_ASSAULT_KEY = 'o-sexual-assault';

export interface CollapsibleModalState {
  /** 모달 열림 여부 */
  isOpen: boolean;
  /** 현재 선택된 아이템 키 */
  selectedItemKey: string | null;
  /** 아이템이 속한 그룹 키 */
  groupKey: string | null;
  /** 증상 트리의 질문들 */
  questions: QuestionResponseDto[];
  /** 모달 내 답변 상태 */
  answers: Record<string, LiveTestingAnswerValue>;
  /** 로딩 중 */
  isLoading: boolean;
  /** 에러 메시지 */
  error: string | null;
}

export interface CollapsibleValue {
  /** 선택된 아이템 키들 */
  selectedKeys: string[];
  /** 각 아이템별 상세 답변 */
  itemAnswers: Record<string, Record<string, LiveTestingAnswerValue>>;
}

export interface UseLiveTestingCollapsibleProps {
  /** 질문의 answerKeys (그룹 또는 아이템 키들) */
  answerKeys: string[];
  /** 질문의 answerOptionsMap */
  answerOptionsMap: Record<string, any> | null;
  /** 현재 값 */
  value: CollapsibleValue;
  /** 값 변경 콜백 */
  onChange: (value: CollapsibleValue) => void;
  /** 현재 로케일 */
  locale: string;
  /** Survey ID (트리 검색용) */
  surveyId?: number;
  /** 번역 데이터 (dataType 확인용) */
  translations?: TranslationResponseDto[];
  /** 증상 트리 내 sx_select 질문 숨김 여부 */
  suppressSxCollectors?: boolean;
  /** 모든 아이템을 treeSx로 강제 처리 (sx_collect_associated용) */
  forceTreeSx?: boolean;
}

export interface UseLiveTestingCollapsibleReturn {
  /** 그룹 목록 (V2QsCollapsible용) */
  groups: V2QsCollapsibleGroup[];
  /** 현재 열린 그룹 키 */
  openGroupKey: string | null;
  /** 모달 상태 */
  modalState: CollapsibleModalState;
  /** 그룹 토글 핸들러 */
  handleGroupToggle: (groupKey: string) => void;
  /** 아이템 클릭 핸들러 (모달 열기) */
  handleItemClick: (groupKey: string, itemKey: string) => void;
  /** 아이템 체크 핸들러 */
  handleItemCheck: (groupKey: string, itemKey: string, checked: boolean) => void;
  /** 특수 옵션 체크 핸들러 */
  handleSpecialOptionCheck: (groupKey: string, checked: boolean) => void;
  /** 모달 닫기 */
  handleModalClose: () => void;
  /** 모달 저장 */
  handleModalSave: () => void;
  /** 모달 내 답변 업데이트 */
  updateModalAnswer: (questionKey: string, value: LiveTestingAnswerValue) => void;
}

/**
 * 옵션 dataType 확인 (treeSx 여부)
 * QuestionCard.tsx와 동일하게 소문자 'treeSx' 사용
 */
function isTreeSxType(
  optionKey: string,
  answerOptionsMap: Record<string, any> | null,
  translations?: TranslationResponseDto[]
): boolean {
  // answerOptionsMap에서 dataType 확인
  const optionData = answerOptionsMap?.[optionKey];
  if (optionData?.dataType === 'treeSx') {
    return true;
  }

  // translations에서 dataType 확인
  const trans = translations?.find((t) => t.key === optionKey);
  if (trans?.dataType === 'treeSx') {
    return true;
  }

  return false;
}

/**
 * Raw key를 사람이 읽을 수 있는 문자열로 변환
 * e.g. "sx-headache" → "Headache", "o-neck-lt" → "Neck Lt"
 */
function humanizeKey(key: string): string {
  return key
    .replace(/^sx-ih-/, '')
    .replace(/^sx-ai-/, '')
    .replace(/^sx-/, '')
    .replace(/^o-/, '')
    .replace(/^q-/, '')
    .replace(/^st-/, '')
    .replace(/^stage-/, '')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

/**
 * 특수 옵션 타입 결정
 */
function getSpecialType(optionKey: string): V2QsCollapsibleItemType | null {
  if (optionKey === NONE_ABOVE_KEY) {
    return 'noneAbove';
  }
  if (optionKey === SEXUAL_ASSAULT_KEY) {
    return 'special';
  }
  return null;
}

export function useLiveTestingCollapsible({
  answerKeys,
  answerOptionsMap,
  value,
  onChange,
  locale,
  surveyId,
  translations,
  suppressSxCollectors = false,
  forceTreeSx = false,
}: UseLiveTestingCollapsibleProps): UseLiveTestingCollapsibleReturn {
  // 현재 열린 그룹 키
  const [openGroupKey, setOpenGroupKey] = useState<string | null>(null);

  // 아이템별 답변 저장 (내부 상태로 관리하여 closure 문제 방지)
  const [internalItemAnswers, setInternalItemAnswers] = useState<Record<string, Record<string, LiveTestingAnswerValue>>>(() => {
    return value?.itemAnswers || {};
  });

  // value.itemAnswers가 변경되면 내부 상태 동기화 (화면 이동 후 복귀 시)
  useEffect(() => {
    const valueItemAnswers = value?.itemAnswers;
    if (valueItemAnswers && Object.keys(valueItemAnswers).length > 0) {
      setInternalItemAnswers(valueItemAnswers);
    }
  }, [value?.itemAnswers]);

  // 모달 상태
  const [modalState, setModalState] = useState<CollapsibleModalState>({
    isOpen: false,
    selectedItemKey: null,
    groupKey: null,
    questions: [],
    answers: {},
    isLoading: false,
    error: null,
  });

  // treeKey -> treeId 매핑 캐시
  const [treeIdCache, setTreeIdCache] = useState<Record<string, number>>({});

  // 로딩 중인 그룹 키
  const [loadingGroupKey, setLoadingGroupKey] = useState<string | null>(null);

  // 그룹별 아이템 목록 캐시 (groupKey -> items)
  const [groupItemsCache, setGroupItemsCache] = useState<Record<string, string[]>>({});

  // 번역 데이터 캐시 (key -> { label, dataType })
  const [translationCache, setTranslationCache] = useState<Record<string, { label: string; dataType?: string }>>({});

  // 번역 데이터 로딩 상태
  const [isLoadingTranslations, setIsLoadingTranslations] = useState(false);

  // 이미 fetch 요청한 키들 추적 (무한 루프 방지)
  const fetchedKeysRef = useRef<Set<string>>(new Set());

  // 번역 데이터 로드 (answerKeys에 대한 label과 dataType 가져오기)
  useEffect(() => {
    if (!surveyId || answerKeys.length === 0) return;

    // 이미 캐시에 있거나 요청한 키는 제외
    const keysToFetch = answerKeys.filter(
      (key) => !translationCache[key] && !answerOptionsMap?.[key]?.dataType && !fetchedKeysRef.current.has(key)
    );
    if (keysToFetch.length === 0) return;

    // 요청할 키들 표시
    keysToFetch.forEach((key) => fetchedKeysRef.current.add(key));

    setIsLoadingTranslations(true);

    translationControllerSearch({
      surveyId,
      keys: keysToFetch,
      locale,
      type: 'patient',
      limit: 1000,
    })
      .then((response) => {
        const transResults = (response as any)?.data || (response as any)?.translations || [];
        const newCache: Record<string, { label: string; dataType?: string }> = {};

        transResults.forEach((t: any) => {
          if (t.key) {
            newCache[t.key] = {
              label: t.value || t.key,
              dataType: t.dataType,
            };
          }
        });

        setTranslationCache((prev) => ({ ...prev, ...newCache }));
      })
      .catch((error) => {
        console.error('Failed to load translations:', error);
      })
      .finally(() => {
        setIsLoadingTranslations(false);
      });
  }, [answerKeys, surveyId, locale, answerOptionsMap]);

  // 그룹 목록 생성
  const groups = useMemo<V2QsCollapsibleGroup[]>(() => {
    const { selectedKeys = [] } = value || {};

    return answerKeys.map((key) => {
      const optionData = answerOptionsMap?.[key];
      const cachedTrans = translationCache[key];
      const rawLabel = (cachedTrans?.label as string) || (optionData?.label as string) || '';
      // If we have a real label (not a raw key), use it directly.
      // Only normalize through the locale function if the label might be a raw key.
      const label = rawLabel && !/^(q|o|sx|st|stage)-[a-z0-9]/i.test(rawLabel)
        ? rawLabel
        : normalizeTranslatedLabelForSurveyLocale({
            locale,
            label: rawLabel,
            fallbackKey: humanizeKey(key),
          });
      const specialType = getSpecialType(key);

      // dataType 확인: answerOptionsMap > translationCache > translations prop
      const dataType = optionData?.dataType || cachedTrans?.dataType;
      const isTreeSx = dataType === 'treeSx' || isTreeSxType(key, answerOptionsMap, translations);

      // 특수 옵션 (NoneAbove, Special)
      if (specialType) {
        return {
          key,
          label,
          type: specialType,
          checked: selectedKeys.includes(key),
          disabled: false,
        };
      }

      // TreeSx 타입이면 단일 아이템으로 처리 (체크박스 형태)
      // forceTreeSx가 true이거나 sx- 접두사가 있는 키도 treeSx로 처리
      const shouldTreatAsTreeSx = isTreeSx || forceTreeSx || key.startsWith('sx-');
      if (shouldTreatAsTreeSx) {
        const hasAnswer = !!internalItemAnswers[key] && Object.keys(internalItemAnswers[key]).length > 0;
        return {
          key,
          label,
          type: 'treeSx' as V2QsCollapsibleItemType,
          items: [
            {
              key,
              label,
              checked: selectedKeys.includes(key),
              hasAnswer,
              disabled: false,
            },
          ],
        };
      }

      // 일반 그룹 - 캐시된 아이템 또는 빈 배열
      const groupItems = groupItemsCache[key] || [];
      const items: V2QsCollapsibleItem[] = groupItems.map((itemKey) => {
        const itemData = answerOptionsMap?.[itemKey];
        const itemCachedTrans = translationCache[itemKey];
        const rawItemLabel = (itemCachedTrans?.label as string) || (itemData?.label as string) || '';
        const itemLabel = rawItemLabel && !/^(q|o|sx|st|stage)-[a-z0-9]/i.test(rawItemLabel)
          ? rawItemLabel
          : normalizeTranslatedLabelForSurveyLocale({
              locale,
              label: rawItemLabel,
              fallbackKey: humanizeKey(itemKey),
            });
        const hasAnswer = !!internalItemAnswers[itemKey] && Object.keys(internalItemAnswers[itemKey]).length > 0;

        return {
          key: itemKey,
          label: itemLabel,
          checked: selectedKeys.includes(itemKey),
          hasAnswer,
          disabled: false,
        };
      });

      return {
        key,
        label,
        type: 'normal' as V2QsCollapsibleItemType,
        items,
      };
    });
  }, [answerKeys, answerOptionsMap, value, groupItemsCache, translations, forceTreeSx, internalItemAnswers, translationCache]);

  // 그룹 토글 핸들러
  const handleGroupToggle = useCallback(
    async (groupKey: string) => {
      // 이미 열려있으면 닫기
      if (openGroupKey === groupKey) {
        setOpenGroupKey(null);
        return;
      }

      // 그룹 열기
      setOpenGroupKey(groupKey);

      // 캐시에 아이템이 없으면 로드
      if (!groupItemsCache[groupKey] && surveyId) {
        setLoadingGroupKey(groupKey);

        try {
          let itemKeys: string[] = [];
          // 그룹 키로 트리 검색하여 아이템 목록 가져오기
          const result = await questionTreeControllerFindByTreeKey({
            surveyId,
            treeKey: groupKey,
          });

          if (result) {
            // 트리의 질문에서 answerKeys 추출 (그룹 내 아이템들)
            const questions = await questionControllerFindByTreeId(result.id, { locale });
            const firstQuestion = questions?.[0];
            itemKeys = firstQuestion?.answerKeys || [];
          } else if (groupKey.startsWith('q-')) {
            // linked question key(q-*)인 경우 question의 answerKeys를 그룹 아이템으로 사용
            const linkedQuestion = await questionControllerFindByKey({
              surveyId,
              questionKey: groupKey,
              locale,
            }).catch(() => null);
            itemKeys = linkedQuestion?.answerKeys || [];
          }

          if (itemKeys.length > 0) {
            setGroupItemsCache((prev) => ({
              ...prev,
              [groupKey]: itemKeys,
            }));

            // 그룹 아이템들에 대한 번역 데이터도 로드
            const keysToFetch = itemKeys.filter((key) => !translationCache[key]);
            if (keysToFetch.length > 0) {
              try {
                const transResponse = await translationControllerSearch({
                  surveyId,
                  keys: keysToFetch,
                  locale,
                  type: 'patient',
                  limit: 1000,
                });

                const itemTransResults = (transResponse as any)?.data || (transResponse as any)?.translations || [];
                const newCache: Record<string, { label: string; dataType?: string }> = {};

                itemTransResults.forEach((t: any) => {
                  if (t.key) {
                    newCache[t.key] = {
                      label: t.value || t.key,
                      dataType: t.dataType,
                    };
                  }
                });

                setTranslationCache((prev) => ({ ...prev, ...newCache }));
              } catch (transError) {
                console.error('Failed to load item translations:', transError);
              }
            }
          }
        } catch (error) {
          console.error('Failed to load group items:', error);
        } finally {
          setLoadingGroupKey(null);
        }
      }
    },
    [openGroupKey, groupItemsCache, surveyId, locale, translationCache]
  );

  // 아이템 클릭 핸들러 (모달 열기)
  const handleItemClick = useCallback(
    async (groupKey: string, itemKey: string) => {
      setModalState({
        isOpen: true,
        selectedItemKey: itemKey,
        groupKey,
        questions: [],
        answers: internalItemAnswers[itemKey] || {},
        isLoading: true,
        error: null,
      });

      try {
        // treeKey로 트리 검색하여 treeId 획득
        let treeId = treeIdCache[itemKey];

        if (!treeId && surveyId) {
          const result = await questionTreeControllerFindByTreeKey({
            surveyId,
            treeKey: itemKey,
          });

          if (result) {
            treeId = result.id;
            setTreeIdCache((prev) => ({ ...prev, [itemKey]: treeId! }));
          }
        }

        // treeId로 질문들 로드
        if (treeId) {
          const questions = await questionControllerFindByTreeId(treeId, { locale });

          // suppressSxCollectors가 true면 sx_select 타입 질문 필터링
          const filteredQuestions = suppressSxCollectors
            ? questions.filter((q) => q.inputType !== 'sx_select')
            : questions;

          setModalState((prev) => ({
            ...prev,
            isLoading: false,
            questions: filteredQuestions,
          }));
        } else {
          // 트리가 없으면 단순 선택으로 처리
          setModalState((prev) => ({
            ...prev,
            isLoading: false,
            error: 'No symptom tree found for this item',
          }));
        }
      } catch (error) {
        setModalState((prev) => ({
          ...prev,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to load questions',
        }));
      }
    },
    [internalItemAnswers, treeIdCache, surveyId, locale, suppressSxCollectors]
  );

  // 아이템 체크 핸들러
  const handleItemCheck = useCallback(
    (groupKey: string, itemKey: string, checked: boolean) => {
      const { selectedKeys = [] } = value || {};
      const groupType = groups.find((group) => group.key === groupKey)?.type;
      const shouldOpenSymptomModal = checked && (groupType === 'treeSx' || itemKey.startsWith('sx-'));

      if (checked) {
        // NoneAbove가 선택되어 있으면 제거
        const newSelectedKeys = [
          ...selectedKeys.filter((k) => k !== NONE_ABOVE_KEY),
          itemKey,
        ];

        onChange({
          selectedKeys: newSelectedKeys,
          itemAnswers: internalItemAnswers,
        });

        if (shouldOpenSymptomModal) {
          handleItemClick(groupKey, itemKey);
        }
      } else {
        // 선택 해제
        const newSelectedKeys = selectedKeys.filter((k) => k !== itemKey);
        const newItemAnswers = { ...internalItemAnswers };
        delete newItemAnswers[itemKey];

        // 내부 상태 업데이트
        setInternalItemAnswers(newItemAnswers);

        onChange({
          selectedKeys: newSelectedKeys,
          itemAnswers: newItemAnswers,
        });
      }
    },
    [value, groups, onChange, internalItemAnswers, handleItemClick]
  );

  // 특수 옵션 체크 핸들러
  const handleSpecialOptionCheck = useCallback(
    (groupKey: string, checked: boolean) => {
      const { selectedKeys = [] } = value || {};

      if (groupKey === NONE_ABOVE_KEY) {
        if (checked) {
          // NoneAbove 선택 시 다른 모든 선택 해제
          setInternalItemAnswers({});
          onChange({
            selectedKeys: [NONE_ABOVE_KEY],
            itemAnswers: {},
          });
        } else {
          // NoneAbove 해제
          onChange({
            selectedKeys: selectedKeys.filter((k) => k !== NONE_ABOVE_KEY),
            itemAnswers: internalItemAnswers,
          });
        }
      } else if (checked) {
        // 기타 특수 옵션 선택
        const newSelectedKeys = [
          ...selectedKeys.filter((k) => k !== NONE_ABOVE_KEY),
          groupKey,
        ];

        onChange({
          selectedKeys: newSelectedKeys,
          itemAnswers: internalItemAnswers,
        });
      } else {
        // 기타 특수 옵션 해제
        onChange({
          selectedKeys: selectedKeys.filter((k) => k !== groupKey),
          itemAnswers: internalItemAnswers,
        });
      }
    },
    [value, onChange, internalItemAnswers]
  );

  // 모달 닫기
  const handleModalClose = useCallback(() => {
    setModalState({
      isOpen: false,
      selectedItemKey: null,
      groupKey: null,
      questions: [],
      answers: {},
      isLoading: false,
      error: null,
    });
  }, []);

  // 모달 저장
  const handleModalSave = useCallback(() => {
    const { selectedItemKey, answers } = modalState;
    if (!selectedItemKey) return;

    const { selectedKeys = [] } = value || {};

    // 아이템 답변 저장 (내부 상태 업데이트)
    const newItemAnswers = {
      ...internalItemAnswers,
      [selectedItemKey]: answers,
    };
    setInternalItemAnswers(newItemAnswers);

    // 선택 상태 업데이트
    let newSelectedKeys: string[];
    if (selectedKeys.includes(selectedItemKey)) {
      newSelectedKeys = selectedKeys;
    } else {
      // NoneAbove 제거 후 추가
      newSelectedKeys = [
        ...selectedKeys.filter((k) => k !== NONE_ABOVE_KEY),
        selectedItemKey,
      ];
    }

    onChange({
      selectedKeys: newSelectedKeys,
      itemAnswers: newItemAnswers,
    });

    handleModalClose();
  }, [modalState, value, onChange, handleModalClose, internalItemAnswers]);

  // 모달 내 답변 업데이트
  const updateModalAnswer = useCallback(
    (questionKey: string, answerValue: LiveTestingAnswerValue) => {
      setModalState((prev) => ({
        ...prev,
        answers: {
          ...prev.answers,
          [questionKey]: answerValue,
        },
      }));
    },
    []
  );

  return {
    groups,
    openGroupKey,
    modalState,
    handleGroupToggle,
    handleItemClick,
    handleItemCheck,
    handleSpecialOptionCheck,
    handleModalClose,
    handleModalSave,
    updateModalAnswer,
  };
}
